// src/components/admin/AdminClientListPage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Container, Typography, Paper, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton,
  TextField, InputAdornment, Chip, Button, Skeleton, Alert,
  Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  Search, Add, Edit, Delete, Visibility, MoreVert,
  Business, Email, Phone, CheckCircle, Cancel,
  TrendingUp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

export default function AdminClientListPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    
    try {
      const clientsData = await adminApi.getClients();
      setClients(clientsData.results || clientsData);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.message || 'فشل في تحميل قائمة العملاء');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };

  const handleViewClient = () => {
    if (selectedClient) {
      navigate(`/admin/clients/${selectedClient.id}`);
    }
    handleMenuClose();
  };

  const handleEditClient = () => {
    if (selectedClient) {
      navigate(`/admin/clients/edit/${selectedClient.id}`);
    }
    handleMenuClose();
  };

  const handleDeleteClient = async () => {
    if (selectedClient && window.confirm(`هل أنت متأكد من حذف العميل "${selectedClient.company_name}"؟`)) {
      try {
        await adminApi.deleteClient(selectedClient.id);
        alert('تم حذف العميل بنجاح');
        fetchClients(); // إعادة تحميل القائمة
      } catch (error) {
        alert(error.message || 'فشل في حذف العميل');
      }
    }
    handleMenuClose();
  };

  // تصفية العملاء حسب البحث
  const filteredClients = clients.filter(client =>
    client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone_number?.includes(searchTerm)
  );

  // حساب العملاء المعروضين
  const displayedClients = filteredClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ar-SA');
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="40%" height={32} />
        </Box>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchClients}>
              إعادة المحاولة
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1" fontWeight="700">
              إدارة العملاء
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/admin/clients/new')}
              sx={{
                background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0284c7, #2563eb)',
                }
              }}
            >
              إضافة عميل جديد
            </Button>
          </Box>
          <Typography variant="body1" color="text.secondary">
            إدارة جميع العملاء وعرض تفاصيلهم
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="البحث في العملاء..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`إجمالي العملاء: ${clients.length}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`البوت يعمل: ${clients.filter(c => c.is_active).length}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`البوت متوقف: ${clients.filter(c => !c.is_active).length}`}
              color="error"
              variant="outlined"
            />
          </Box>
        </Paper>

        {/* Clients Table */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>الشركة</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>البريد الإلكتروني</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>رقم الهاتف</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>الحالة</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>تاريخ الإنشاء</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>التوكنز المستهلكة</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد عملاء'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedClients.map((client) => (
                    <TableRow
                      key={client.id}
                      sx={{
                        '&:hover': { bgcolor: 'grey.50' },
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/admin/clients/${client.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business color="primary" />
                          <Typography variant="body1" fontWeight="600">
                            {client.company_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email color="action" />
                          <Typography variant="body2">
                            {client.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone color="action" />
                          <Typography variant="body2">
                            {client.phone_number || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.is_active ? 'البوت يعمل' : 'البوت متوقف'}
                          color={client.is_active ? 'success' : 'error'}
                          icon={client.is_active ? <CheckCircle /> : <Cancel />}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(client.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="600">
                            {client.total_tokens_consumed ? client.total_tokens_consumed.toLocaleString() : '0'}
                          </Typography>
                          <TrendingUp color="success" fontSize="small" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, client);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredClients.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="صفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleViewClient}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>عرض التفاصيل</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditClient}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>تعديل</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClient} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>حذف</ListItemText>
          </MenuItem>
        </Menu>
      </Container>
    </motion.div>
  );
}